/**
 * Phase 22 + Batch-1 — Tenant-wide "Alle Werbung stoppen" button.
 *
 * Contract (Batch-1 rework):
 *   - Single, prominent Emergency-Stop pill in the topbar.
 *   - Clicking opens a short confirm dialog — no typed slug, no
 *     reason field.
 *   - Confirmed → POST /safety/emergency-stop {confirm: true}.
 *   - Two-stage UI wording based on `emergency_state`:
 *       stop_requested (Amber): "Stopp angefordert — externe Anbieter
 *                                bestätigen …"
 *       stopped        (Rot)  : "Alle Werbung gestoppt"
 *   - Resume affordance only visible while stopped.
 *
 * Data:
 *   - GET  /api/v1/safety/status              (polls every 15s)
 *   - POST /api/v1/safety/emergency-stop      {confirm: true}
 *   - POST /api/v1/safety/resume-from-emergency
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, ShieldAlert, Loader2, PlayCircle } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Badge } from "../ui/badge";

export default function EmergencyStopButton() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const r = await api.get("/safety/status",
        { skipPermissionToast: true });
      setStatus(r.data);
    } catch (e) {
      /* silent — user may lack permission */
    }
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, []);

  const stopped = !!status?.emergency_stopped_at;
  const emState = status?.emergency_state;                // stop_requested | stopped
  const pending = status?.pending_external_stops || 0;

  async function submitStop() {
    setBusy(true);
    try {
      const r = await api.post("/safety/emergency-stop",
        { confirm: true });
      const cascade = r?.data?.cascade || {};
      if (cascade.state === "stopped" || cascade.state === "no_campaigns") {
        toast.error("Alle Werbung gestoppt. Neue Publishes sind blockiert.");
      } else {
        toast.error(
          `Stopp angefordert — ${cascade.pending_external_stops || 0} externe`
          + " Stopps warten auf Anbieter-Bestätigung.");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail?.hint
        || e?.response?.data?.detail?.error || "Stopp fehlgeschlagen";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function submitResume() {
    setBusy(true);
    try {
      await api.post("/safety/resume-from-emergency");
      toast.success("Publishing wieder aktiviert.");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Fortsetzung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  if (!status) return null;

  // Amber pill while stop is requested but not fully confirmed by
  // external providers; solid red once every external provider has
  // acknowledged the stop.
  const isRequested = stopped && emState === "stop_requested";
  const isStopped   = stopped && emState !== "stop_requested";

  return (
    <>
      {isStopped ? (
        <Button
          size="sm"
          variant="outline"
          className="gap-2 border-rose-500 bg-rose-50 text-rose-700 hover:bg-rose-100
                        dark:bg-rose-950 dark:text-rose-200 dark:hover:bg-rose-900"
          data-testid="emergency-resume-btn"
          onClick={() => setOpen(true)}
        >
          <ShieldAlert className="size-4" />
          <span className="hidden sm:inline">Alle Werbung gestoppt</span>
          <Badge variant="secondary"
                    className="ml-1 text-[10px] px-1 py-0 font-mono">ON</Badge>
        </Button>
      ) : isRequested ? (
        <Button
          size="sm"
          variant="outline"
          className="gap-2 border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100
                        dark:bg-amber-950 dark:text-amber-200 dark:hover:bg-amber-900"
          data-testid="emergency-requested-btn"
          onClick={() => setOpen(true)}
        >
          <Loader2 className="size-4 animate-spin" />
          <span className="hidden sm:inline">Stopp angefordert</span>
          {pending > 0 && (
            <Badge variant="secondary"
                     className="ml-1 text-[10px] px-1 py-0 font-mono">
              {pending}
            </Badge>
          )}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="gap-2 border-rose-400 text-rose-700 hover:bg-rose-50
                        dark:text-rose-300 dark:hover:bg-rose-950"
          data-testid="emergency-stop-btn"
          onClick={() => setOpen(true)}
        >
          <AlertOctagon className="size-4" />
          <span className="hidden sm:inline">Alle Werbung stoppen</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emergency-stop-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {stopped ? (
                <><PlayCircle className="size-5 text-emerald-600" />
                  Publishing wieder aktivieren</>
              ) : (
                <><AlertOctagon className="size-5 text-rose-600" />
                  Alle Werbung sofort stoppen</>
              )}
            </DialogTitle>
            <DialogDescription>
              {stopped
                ? `Aktiv gestoppt seit ${status.emergency_stopped_at}. `
                  + (isRequested
                      ? `Es warten noch ${pending} externe Stopps auf `
                        + "Anbieter-Bestätigung. Die Reconciler-Schleife "
                        + "arbeitet sie automatisch ab."
                      : "Alle externen Anbieter haben den Stopp bestätigt. "
                        + "Publishing kann jederzeit wieder aktiviert werden.")
                : "Blockiert alle Publishes und pausiert laufende "
                  + "Kampagnen bei allen Ad-Anbietern. Bestätigung genügt "
                  + "— keine Eingabe nötig."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}
                       data-testid="emergency-cancel-btn">
              Abbrechen
            </Button>
            {stopped ? (
              <Button
                onClick={submitResume}
                disabled={busy}
                data-testid="emergency-resume-confirm-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {busy && <Loader2 className="size-4 mr-1 animate-spin" />}
                Fortsetzen
              </Button>
            ) : (
              <Button
                onClick={submitStop}
                disabled={busy}
                data-testid="emergency-stop-confirm-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                {busy && <Loader2 className="size-4 mr-1 animate-spin" />}
                Ja, alle Werbung stoppen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
